// <copyright>Copyright (c) Microsoft Corporation. All rights reserved.</copyright>

import * as ArrayUtils from "Utils/Collections/ArrayUtils";
import * as MapUtils from "Utils/Collections/MapUtils";
import { type DeepPartial } from "Utils/DeepPartial";
import { type Mutable } from "Utils/Mutable";
import { type StrictOmit } from "Utils/StrictOmit";

export function areEqual<T>(
    left: T | null | undefined,
    right: T | null | undefined,
    equalityCheckWhenBothAreDefined: (_left: T, _right: T) => boolean
): boolean {
    if (left === undefined && right === undefined) {
        return true;
    }

    if (left === null && right === null) {
        return true;
    }

    if (left == null || right == null) {
        return false;
    }

    return equalityCheckWhenBothAreDefined(left, right);
}

export function areShallowEqual<T>(left: T, right: T): boolean {
    if (!isObject(left) && !isObject(right)) {
        return left === right;
    }

    if (isObject(left) && isObject(right)) {
        return (
            getKeys(left).every(key => left[key] === right[key]) &&
            getKeys(right).every(key => left[key] === right[key])
        );
    }

    return false;
}

export function asStringOrUndefined<T>(value: T): string | undefined {
    if (typeof value === "string") {
        return value;
    }

    return undefined;
}

export function clone<T extends object>(value: T): Mutable<T> {
    if (value) {
        const result: any = Array.isArray(value) ? [] : {};

        for (const key of Object.keys(value)) {
            result[key] = (value as any)[key];
        }

        return result;
    }

    return value;
}

export function conditional<T>(condition: boolean, value: T): T | undefined {
    return condition ? value : undefined;
}

export function createObjectKey<T>(value: T): string {
    return JSON.stringify(deepSortProperties(value));
}

export function deepClone<T>(value: T): T {
    if (value == null || typeof value !== "object") {
        return value;
    }

    const result: any = Array.isArray(value) ? [] : {};

    for (const key of Object.keys(value)) {
        let property: any = (value as any)[key];

        if (isObject(property)) {
            property = deepClone(property);
        }

        result[key] = property;
    }

    return result;
}

export function deepReplaceProperties<T>(
    value: T,
    replacement: DeepPartial<T> | undefined,
    options?: { ignoreUndefinedValuesInReplacement?: boolean }
): T {
    if (replacement) {
        value = value ?? ({} as any);
        const result: any = { ...value };

        for (const [key, property] of Object.entries(replacement)) {
            if (property !== undefined || !options?.ignoreUndefinedValuesInReplacement) {
                result[key] = isRecord(property)
                    ? deepReplaceProperties((value as any)[key], property as any, options)
                    : property;
            }
        }

        return result;
    }

    return value;
}

export function deepSortProperties<T>(value: T): T {
    if (value == null || typeof value !== "object") {
        return value;
    }

    const result: any = Array.isArray(value) ? [] : {};

    for (const key of ArrayUtils.stableSort(Object.keys(value))) {
        let property: any = (value as any)[key];

        if (isObject(property)) {
            property = deepSortProperties(property);
        }

        result[key] = property;
    }

    return result;
}

export function extend<T, K extends keyof T>(base: StrictOmit<T, K>, extension: Pick<T, K>): T {
    // The cast is required because of a bug in TypeScript:
    // Issue 28884: Pick<T, Exclude<keyof T, K>> & Pick<T, Extract<keyof T, K>> should be assignable to T
    // https://github.com/Microsoft/TypeScript/issues/28884
    return {
        ...base,
        ...extension,
    } as T;
}

export function filterProperties<T>(value: Partial<T>, predicate: (propertyValue: any) => boolean): Partial<T> {
    const result: any = {};

    for (const propertyName of Object.keys(value)) {
        const propertyValue: any = (value as any)[propertyName];

        if (predicate(propertyValue)) {
            result[propertyName] = propertyValue;
        }
    }

    return result;
}

export function forceCast<TNewType>(value: any): TNewType {
    return value as unknown as TNewType;
}

export function getDefinedProperties<T>(value: Partial<T>): Partial<T> {
    if (value == null || typeof value !== "object") {
        return {} as Partial<T>;
    }

    return filterProperties(value, propertyValue => propertyValue !== undefined);
}

export function getDefinedPropertyNames(value: any): ReadonlyArray<string> {
    if (value == null) {
        return [];
    }

    try {
        return getDefinedPropertyNamesInternal({ value, path: undefined });
    } catch (error) {
        return [];
    }
}

function getDefinedPropertyNamesInternal(params: { value: any; path: string | undefined }): ReadonlyArray<string> {
    const { value, path } = params;

    const definedPropertyNames: string[] = [];

    for (const propertyName of Object.keys(value)) {
        if (typeof propertyName === "string") {
            const property: any = value[propertyName];
            const fullPropertyName: string = path ? `${path}.${propertyName}` : propertyName;

            if (property !== undefined) {
                definedPropertyNames.push(fullPropertyName);
            }

            if (typeof property === "object" && property != null) {
                definedPropertyNames.push(
                    ...getDefinedPropertyNamesInternal({
                        value: property,
                        path: fullPropertyName,
                    })
                );
            }
        }
    }

    return definedPropertyNames;
}

export function getKeys<T>(value: T & object): ReadonlyArray<keyof T> {
    return Object.keys(value) as (keyof T)[];
}

export function getEntries<T>(value: T & object): ReadonlyArray<[keyof T, T[keyof T]]> {
    return Object.entries(value) as [keyof T, T[keyof T]][];
}

/** Gets a property in an object of return type TReturn or the default value. */
export function getPropertyOrDefault<TReturn>(
    value: Record<string | number, any> | null | undefined,
    property: string | number,
    defaultValue: TReturn
): TReturn {
    // Check for null & undefined and that object really is an object with its own properties.
    return value && isRecord(value) && value.hasOwnProperty(property) ? value[property] ?? defaultValue : defaultValue;
}

/* Simply returns the value of the given key in a type-safe Record object.
 *  Used to workaround shortcoming of `no-element-access-expressions` rule for `Record` types.
 */
export function getProperty<K extends string, V>(record: Record<K, V>, key: K): V {
    return record[key];
}

export function getPropertiesByKeys(obj: object, keys: ReadonlyArray<string>): Map<string, unknown> {
    return MapUtils.filter(new Map<string, unknown>(Object.entries(obj)), (key, _value) => keys.includes(key));
}

export function handleUndefined<T1, T2>(
    transformFunction: (value: T1) => T2
): (value: T1 | undefined) => T2 | undefined {
    return value => {
        if (value == null) {
            return undefined;
        }

        return transformFunction(value);
    };
}

/** Check to see if a value is a JavaScript object */
export function isObject<T1>(value: T1): value is T1 & object {
    // Explicitly check for null given that typeof null === "object" since the beginning of JavaScript.
    return value !== null && typeof value === "object";
}

/** Check to see if an object is a Record<> and not an Array, RegExp, Date, etc. */
export function isRecord(value: any): boolean {
    // Every possible check to get a Record and not an Array, RegExp, Date, etc.
    return isObject(value) && !Array.isArray(value) && value.constructor === Object;
}

export function toString<T extends Object>(value: T | null | undefined): string | undefined {
    if (value == null) {
        return undefined;
    }

    return value.toString();
}

export function transform<T1, T2>(value: T1, transformFunction: (value: T1) => T2): T2 {
    return transformFunction(value);
}

export function transformNullable<T1, T2>(value: T1 | undefined, transformFunction: (value: T1) => T2): T2 | undefined {
    if (value == null) {
        return undefined;
    }

    return transformFunction(value);
}

export function transformIf<T>(value: T, condition: boolean, transformFunction: (value: T) => T): T {
    if (condition) {
        return transformFunction(value);
    }

    return value;
}

export function without<T, K extends keyof T & string>(value: T, keys: ReadonlyArray<K>): StrictOmit<T, K> {
    if (value == null) {
        return value;
    }

    const result: any = {};

    for (const key of Object.keys(value)) {
        if (!ArrayUtils.contains(keys, key)) {
            result[key] = (value as any)[key];
        }
    }

    return result;
}
